import React, {
    MutableRefObject,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FlatList } from 'react-native';
import { SharedValue, useSharedValue } from 'react-native-reanimated';

import { PlayerInfoWithProjectionsFragment } from '@/api/events/query.generated';

import { POPULAR_PLAYERS_FILTER } from '../utils/players';

export type PlayerFiltersState = {
    playerProjectionFilter: string | undefined;
    playerProjectionLabel: string | undefined;
    playerPositionFilter: string | undefined;
    playerPositionDescription: string | undefined;
};

export type PlayerFiltersActions = {
    setPlayerProjectionFilter: (value?: string, label?: string) => void;
    resetProjectionsFilter: () => void;
    setPlayerPositionFilter: (value: string, description: string) => void;
    resetPositionFilter: () => void;
    resetAllFilters: () => void;
};

export type PlayerFiltersScrollControls = {
    listRef: MutableRefObject<FlatList<any> | null>;
    listScrollOffset: SharedValue<number>;
    filtersStickPosition: SharedValue<number>;
    scrollToFilters: () => void;
};

const PlayerFiltersStateContext = createContext<PlayerFiltersState | undefined>(undefined);
const PlayerFiltersActionsContext = createContext<PlayerFiltersActions | undefined>(undefined);
const PlayerFiltersScrollControlsContext = createContext<PlayerFiltersScrollControls | undefined>(undefined);

const DEFAULT_FILTERS_STATE: PlayerFiltersState = {
    playerProjectionFilter: POPULAR_PLAYERS_FILTER,
    playerProjectionLabel: undefined,
    playerPositionFilter: undefined,
    playerPositionDescription: undefined,
};

interface PlayerFiltersProviderProps {
    children: ReactNode;
}

export const PlayerFiltersProvider: React.FC<PlayerFiltersProviderProps> = ({ children }) => {
    const listRef = useRef<FlatList | null>(null);
    const filtersStickPosition = useSharedValue(0);
    const listScrollOffset = useSharedValue(0);

    const [state, setState] = useState<PlayerFiltersState>(DEFAULT_FILTERS_STATE);

    const setPlayerProjectionFilter = useCallback((value?: string, label?: string) => {
        setState(prevState => ({
            ...prevState,
            playerProjectionFilter: value,
            playerProjectionLabel: label,
        }));
    }, []);

    const resetProjectionsFilter = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            playerProjectionFilter: DEFAULT_FILTERS_STATE.playerProjectionFilter,
            playerProjectionLabel: DEFAULT_FILTERS_STATE.playerProjectionLabel,
        }));
    }, []);

    const setPlayerPositionFilter = useCallback((value: string, description: string) => {
        setState(prevState => ({
            ...prevState,
            playerPositionFilter: value,
            playerPositionDescription: description,
        }));
    }, []);

    const resetPositionFilter = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            playerPositionFilter: undefined,
            playerPositionDescription: undefined,
        }));
    }, []);

    const resetAllFilters = useCallback(() => {
        setState(DEFAULT_FILTERS_STATE);
    }, []);

    const scrollToFilters = useCallback(() => {
        if (listScrollOffset.value > filtersStickPosition.value) {
            listRef?.current?.scrollToOffset({
                animated: false,
                offset: filtersStickPosition.value + 1, // + 1 to make sure the header displays the game time on event page
            });
        }
    }, [filtersStickPosition, listScrollOffset]);

    const actionsValue: PlayerFiltersActions = useMemo(
        () => ({
            setPlayerProjectionFilter,
            resetProjectionsFilter,
            setPlayerPositionFilter,
            resetPositionFilter,
            resetAllFilters,
        }),
        [
            setPlayerProjectionFilter,
            resetProjectionsFilter,
            setPlayerPositionFilter,
            resetPositionFilter,
            resetAllFilters,
        ]
    );

    const refsValue: PlayerFiltersScrollControls = useMemo(
        () => ({
            listRef,
            filtersStickPosition,
            listScrollOffset,
            scrollToFilters,
        }),
        [filtersStickPosition, listScrollOffset, scrollToFilters]
    );

    return (
        <PlayerFiltersScrollControlsContext.Provider value={refsValue}>
            <PlayerFiltersActionsContext.Provider value={actionsValue}>
                <PlayerFiltersStateContext.Provider value={state}>{children}</PlayerFiltersStateContext.Provider>
            </PlayerFiltersActionsContext.Provider>
        </PlayerFiltersScrollControlsContext.Provider>
    );
};

export const usePlayerFiltersState = (): PlayerFiltersState => {
    const context = useContext(PlayerFiltersStateContext);
    if (context === undefined) {
        throw new Error('usePlayerFiltersState must be used within a PlayerFiltersProvider');
    }
    return context;
};

export const usePlayerFiltersActions = (): PlayerFiltersActions => {
    const context = useContext(PlayerFiltersActionsContext);
    if (context === undefined) {
        throw new Error('usePlayerFiltersActions must be used within a PlayerFiltersProvider');
    }
    return context;
};

export const usePlayerFiltersScrollControls = (): PlayerFiltersScrollControls => {
    const context = useContext(PlayerFiltersScrollControlsContext);
    if (context === undefined) {
        throw new Error('usePlayerFiltersScrollControls must be used within a PlayerFiltersProvider');
    }
    return context;
};

const specialPositionFilter = (playerPositionFilter: string, playerPosition: string) => {
    return playerPositionFilter === 'P' && (playerPosition === 'RP' || playerPosition === 'SP');
};

export const filterByProjectionFunction =
    (playerProjectionFilter: string | undefined) => (player: PlayerInfoWithProjectionsFragment) => {
        return (
            !playerProjectionFilter || player.projections?.some(projection => projection.key === playerProjectionFilter)
        );
    };

export const filterByPositionFunction =
    (playerPositionFilter: string | undefined) => (player: PlayerInfoWithProjectionsFragment) => {
        return (
            !playerPositionFilter ||
            player.position === playerPositionFilter ||
            specialPositionFilter(playerPositionFilter, player.position)
        );
    };
